/**
 * The select that sits beside `AdminInput` in every filter bar (`.m-select` in
 * the prototype's modals.css), sharing its control chrome so the two line up.
 *
 * A native `<select>`, deliberately. The prototype builds a searchable combobox
 * — a text input over a filtered menu with its own hover index, outside-click
 * handling and arrow-key navigation — and that is a genuinely different
 * component: a typeahead. Reimplementing its keyboard model here would be a
 * worse version of what the platform already ships correctly, and none of the
 * filters this kit is built for has enough options to need searching. The
 * catalogue's competition picker is the one place that might; CAE-1555 owns that
 * screen and can grow a `SearchSelect` beside this if it does.
 */
import Box from '@mui/material/Box';
import { useId, type ChangeEvent } from 'react';
import type { Theme } from '@mui/material/styles';
import { controlSx, fieldLabelSx } from './admin-input';
import { ChevronDownIcon } from './line-icons';

export interface AdminSelectOption {
  value: string;
  label: string;
}

interface AdminSelectProps {
  label: string;
  hideLabel?: boolean;
  options: AdminSelectOption[];
  value: string;
  onChange: (value: string) => void;
  /** The empty option's wording — "All", "All sports", "Any market state". */
  allLabel?: string;
  /**
   * Drop the empty option, for a select that is a form field rather than a
   * filter (CAE-1594's Reel count, which is 3 or 4 and never neither).
   *
   * A filter's empty option *is* the "no filter" value, so it is the default.
   * On a form it is a trap: the operator can choose it, and the screen then
   * has to invent a meaning for a value the API will not take.
   */
  required?: boolean;
  /**
   * What is wrong with the choice, shown under the control and announced — the
   * same channel `AdminInput` has (CAE-1594).
   *
   * Without it a screen whose API can refuse this field has nowhere to put the
   * refusal: the save stops, nothing on screen changes, and the operator is
   * left to guess. A string rather than a boolean, for the reason `AdminInput`
   * gives — a merely *red* field says something is wrong and not what.
   */
  error?: string;
  disabled?: boolean;
}

export const AdminSelect = ({
  label,
  hideLabel,
  options,
  value,
  onChange,
  allLabel = 'All',
  required,
  error,
  disabled,
}: AdminSelectProps) => {
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
      {hideLabel ? null : (
        <Box component="label" htmlFor={id} sx={fieldLabelSx}>
          {label}
        </Box>
      )}
      <Box
        sx={[
          controlSx,
          { position: 'relative' },
          // The error edge replaces the resting one but not the focus one, so a
          // field being corrected does not read as going dead mid-edit. Same
          // treatment as `AdminInput`.
          ...(error
            ? [
                (theme: Theme) => ({
                  borderColor: theme.tokens.color.danger,
                  '&:focus-within': {
                    borderColor: theme.tokens.color.danger,
                    boxShadow: `0 0 0 3px ${theme.tokens.color.dangerSoft}`,
                  },
                }),
              ]
            : []),
        ]}
      >
        <Box
          component="select"
          id={id}
          value={value}
          required={required}
          disabled={disabled}
          aria-label={hideLabel ? label : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange(event.target.value)}
          sx={(theme: Theme) => ({
            flex: 1,
            minWidth: 0,
            border: 0,
            outline: 'none',
            background: 'transparent',
            // The native arrow is replaced by the handoff's chevron below.
            appearance: 'none',
            paddingRight: '22px',
            fontFamily: theme.tokens.font.sans,
            fontSize: '13.5px',
            color: value ? theme.tokens.color.ink : theme.tokens.color.ink4,
            fontWeight: value ? 500 : 400,
            cursor: disabled ? 'not-allowed' : 'pointer',
          })}
        >
          {required ? null : <option value="">{allLabel}</option>}
          {/* A value with no matching option — a deep link read before the options
              have loaded — otherwise falls back to displaying the empty option, so
              the bar reads "All sports" while the parent is still filtering by
              football. Carrying the value as its own option keeps what is shown
              and what is being queried the same thing; it disappears of its own
              accord once the real options arrive.

              `required` is the other half of that: it drops the empty option, so
              a required select handed `""` would show its first real option as
              selected while the parent still held `""` — and no change event
              ever fires to reconcile them, so a save would send the empty
              string. Carrying `""` as its own option keeps the two in step. */}
          {(value || required) && !options.some((option) => option.value === value) ? (
            <option value={value}>{value}</option>
          ) : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Box>
        <ChevronDownIcon
          aria-hidden
          sx={(theme: Theme) => ({
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            fontSize: '13px',
            color: theme.tokens.color.ink4,
          })}
        />
      </Box>
      {error ? (
        <Box
          id={errorId}
          sx={(theme: Theme) => ({
            fontSize: '12px',
            lineHeight: 1.45,
            color: theme.tokens.color.danger,
          })}
        >
          {error}
        </Box>
      ) : null}
    </Box>
  );
};
